import React from "react";
import { Document, Page, StyleSheet, Text, View } from "@react-pdf/renderer";
import { EMPTY, NOT_DOCUMENTED, buildEmsSheetData } from "./emsSheetData";

/**
 * EMS Transfer Sheet.
 *
 * Print-first by design: black on white, rules and boxes instead of fills, and
 * colour used only as redundancy on the two fields that can hurt someone
 * (code status and allergies). These sheets travel by fax and photocopier, so
 * tinted panels and light grey meta text do not survive the trip.
 *
 * Nothing is inferred. A field the chart does not answer prints as
 * "Not documented", never as "None".
 */

const INK = "#111111";
const INK_2 = "#3d3d3d";
const INK_3 = "#6b6b6b";
const RULE = "#c9c9c9";
const CRIT = "#B42318";

// Rows kept inside the two-column band. Anything past this flows into a
// full-width "continued" section, which paginates cleanly row by row instead of
// splitting a flex row across a page break.
const MED_ROWS_INLINE = 14;
const DIAGNOSES_INLINE = 12;

const styles = StyleSheet.create({
  page: {
    backgroundColor: "#ffffff",
    color: INK,
    paddingTop: 28,
    paddingBottom: 28,
    paddingHorizontal: 32,
    fontSize: 8.6,
    fontFamily: "Helvetica",
    lineHeight: 1.25,
  },

  /* masthead */
  mast: {
    flexDirection: "row",
    alignItems: "flex-end",
    justifyContent: "space-between",
    borderBottomWidth: 2.5,
    borderBottomColor: INK,
    paddingBottom: 5,
  },
  mastLeft: { flex: 1, paddingRight: 14 },
  mastTitle: { fontSize: 14, fontFamily: "Helvetica-Bold", letterSpacing: -0.3 },
  mastOrg: { fontSize: 8, color: INK_2, marginTop: 3 },
  mastMeta: { width: 180, textAlign: "right", fontSize: 7, color: INK_3 },
  mastMetaStrong: { fontSize: 7.5, color: INK, fontFamily: "Helvetica-Bold" },

  /* continuation header, pages 2+ */
  contBar: {
    flexDirection: "row",
    justifyContent: "space-between",
    borderBottomWidth: 1.5,
    borderBottomColor: INK,
    paddingBottom: 3,
    marginBottom: 8,
  },
  contName: { fontSize: 9, fontFamily: "Helvetica-Bold" },
  contMeta: { fontSize: 7, color: INK_3 },

  /* identity */
  ident: {
    flexDirection: "row",
    alignItems: "flex-end",
    justifyContent: "space-between",
    paddingTop: 6,
    paddingBottom: 5,
    borderBottomWidth: 1,
    borderBottomColor: RULE,
  },
  identName: {
    fontSize: 19,
    fontFamily: "Helvetica-Bold",
    letterSpacing: -0.4,
    flex: 1,
    paddingRight: 12,
  },
  identList: { flexDirection: "row" },
  identCell: { marginLeft: 14 },
  identLabel: {
    fontSize: 6,
    fontFamily: "Helvetica-Bold",
    letterSpacing: 0.6,
    textTransform: "uppercase",
    color: INK_3,
  },
  identValue: { fontSize: 9.5, fontFamily: "Helvetica-Bold", marginTop: 1 },

  /* critical band */
  crit: {
    flexDirection: "row",
    borderWidth: 2.5,
    borderColor: INK,
    marginTop: 8,
  },
  critCell: {
    paddingTop: 5,
    paddingBottom: 6,
    paddingHorizontal: 9,
    borderRightWidth: 1,
    borderRightColor: INK,
  },
  critCellLast: { borderRightWidth: 0 },
  critLabel: {
    fontSize: 6.2,
    fontFamily: "Helvetica-Bold",
    letterSpacing: 0.6,
    textTransform: "uppercase",
    color: INK_2,
  },
  critValue: {
    fontSize: 12,
    fontFamily: "Helvetica-Bold",
    marginTop: 3,
    letterSpacing: -0.2,
  },
  critValueSmall: { fontSize: 8.5, fontFamily: "Helvetica-Bold", marginTop: 3 },
  critNote: { fontSize: 6.6, color: INK_3, marginTop: 2 },
  flag: { color: CRIT },
  undoc: {
    marginTop: 3,
    alignSelf: "flex-start",
    borderWidth: 1.5,
    borderColor: CRIT,
    paddingHorizontal: 4,
    paddingVertical: 1,
  },
  undocText: {
    fontSize: 8.5,
    fontFamily: "Helvetica-Bold",
    color: CRIT,
    textTransform: "uppercase",
  },
  // Same marker, sized to sit inside a key/value or identity row without
  // growing it. A blank field has to read as "nobody recorded this", so it
  // never prints a bare dash on the sheet.
  undocInline: {
    alignSelf: "flex-start",
    borderWidth: 1,
    borderColor: CRIT,
    paddingHorizontal: 2.5,
  },
  undocInlineText: {
    fontFamily: "Helvetica-Bold",
    color: CRIT,
    textTransform: "uppercase",
    lineHeight: 1.15,
  },

  /* body columns */
  cols: { flexDirection: "row", marginTop: 8 },
  colLeft: { width: "53%", paddingRight: 8 },
  colRight: { width: "47%", paddingLeft: 8 },

  section: { marginBottom: 6 },
  sectionHead: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "flex-end",
    borderBottomWidth: 1.5,
    borderBottomColor: INK,
    paddingBottom: 2,
    marginBottom: 4,
  },
  sectionTitle: {
    fontSize: 7,
    fontFamily: "Helvetica-Bold",
    letterSpacing: 0.8,
    textTransform: "uppercase",
  },
  sectionAside: { fontSize: 6.2, color: INK_3 },

  /* key / value rows */
  kvRow: {
    flexDirection: "row",
    paddingVertical: 1.9,
    borderBottomWidth: 0.5,
    borderBottomColor: RULE,
  },
  kvRowLast: { borderBottomWidth: 0 },
  kvLabel: { width: "38%", color: INK_3, fontSize: 7.6, paddingRight: 6 },
  kvValue: { width: "62%", fontSize: 8.1, fontFamily: "Helvetica-Bold" },
  kvValueGap: { width: "62%" },

  /* medication table */
  medHeadRow: {
    flexDirection: "row",
    borderBottomWidth: 1,
    borderBottomColor: INK,
    paddingBottom: 2,
  },
  medHead: {
    fontSize: 6.2,
    fontFamily: "Helvetica-Bold",
    letterSpacing: 0.4,
    textTransform: "uppercase",
    color: INK_2,
    paddingRight: 4,
  },
  medRow: {
    flexDirection: "row",
    paddingVertical: 2.4,
    borderBottomWidth: 0.5,
    borderBottomColor: RULE,
  },
  medCell: { fontSize: 7.8, paddingRight: 4 },
  medName: { fontSize: 7.8, fontFamily: "Helvetica-Bold" },
  medNameWrap: { flexDirection: "row", alignItems: "flex-start" },
  narcotic: {
    borderWidth: 1,
    borderColor: CRIT,
    marginLeft: 3,
    paddingHorizontal: 1.5,
  },
  narcoticText: { fontSize: 6, fontFamily: "Helvetica-Bold", color: CRIT },
  medFoot: { fontSize: 6.8, color: INK_3, marginTop: 3 },

  /* two-up lists */
  listGrid: { flexDirection: "row", flexWrap: "wrap" },
  listItem: {
    width: "50%",
    flexDirection: "row",
    alignItems: "flex-start",
    paddingRight: 10,
    paddingVertical: 1.4,
  },
  listBullet: {
    width: 3.5,
    height: 3.5,
    backgroundColor: INK,
    marginTop: 3.2,
    marginRight: 5,
  },
  listBulletMuted: { backgroundColor: INK_3 },
  listText: { flex: 1, fontSize: 8, fontFamily: "Helvetica-Bold" },
  listTextMuted: { flex: 1, fontSize: 8, color: INK_2 },
  emptyNote: { fontSize: 7.6, color: INK_3, fontFamily: "Helvetica-Oblique" },

  /* attachments */
  attRow: { flexDirection: "row", alignItems: "flex-start", paddingVertical: 1.4 },
  // No flex centring here. A 9x9 box with a 1.3 border leaves 6.4pt of inner
  // height, and react-pdf drops a text run outright when the line box does not
  // fit its centred parent, so the tick silently vanished. The mark is sized to
  // fit and centred with textAlign instead.
  box: {
    width: 9,
    height: 9,
    borderWidth: 1.3,
    borderColor: INK,
    marginTop: 1,
    marginRight: 6,
  },
  boxMark: {
    fontSize: 6,
    fontFamily: "Helvetica-Bold",
    lineHeight: 1,
    textAlign: "center",
  },
  attText: { flex: 1, fontSize: 7.8 },

  /* at scene */
  scene: { borderWidth: 2.5, borderColor: INK, padding: 8, marginTop: 2 },
  sceneHead: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "flex-end",
    borderBottomWidth: 1,
    borderBottomColor: INK,
    paddingBottom: 2,
    marginBottom: 5,
  },
  fieldLabel: {
    fontSize: 6.2,
    fontFamily: "Helvetica-Bold",
    letterSpacing: 0.6,
    textTransform: "uppercase",
    color: INK_2,
    marginBottom: 1,
  },
  writeLine: {
    borderBottomWidth: 1,
    borderBottomColor: INK,
    minHeight: 13,
    marginBottom: 5,
    justifyContent: "flex-end",
  },
  writeValue: { fontSize: 8.4, fontFamily: "Helvetica-Bold", paddingBottom: 1 },
  gridRow: { flexDirection: "row" },
  gridCell: { flex: 1, paddingRight: 10 },
  inlineChecks: { flexDirection: "row", alignItems: "center", marginBottom: 7 },
  inlineCheckText: { fontSize: 7.6, fontFamily: "Helvetica-Bold", marginRight: 16 },

  /* footer */
  footer: {
    position: "absolute",
    left: 32,
    right: 32,
    bottom: 12,
    flexDirection: "row",
    justifyContent: "space-between",
    borderTopWidth: 0.5,
    borderTopColor: RULE,
    paddingTop: 4,
  },
  footerText: { fontSize: 6.2, color: INK_3 },

  /* blank packet */
  rule: { borderBottomWidth: 1, borderBottomColor: INK, height: 11 },
  stampWrap: {
    alignSelf: "flex-end",
    borderWidth: 1.5,
    borderColor: INK,
    paddingHorizontal: 5,
    marginTop: 2,
  },
  stampText: {
    fontSize: 6.5,
    fontFamily: "Helvetica-Bold",
    letterSpacing: 0.9,
    textTransform: "uppercase",
  },
});

const Rule = ({ height, style }) => (
  <View style={[styles.rule, height ? { height } : null, style]} />
);

const Undocumented = ({ size = 6.6, style }) => (
  <View style={[styles.undocInline, style]}>
    <Text style={[styles.undocInlineText, { fontSize: size }]}>{NOT_DOCUMENTED}</Text>
  </View>
);

const SectionHead = ({ title, aside }) => (
  <View style={styles.sectionHead}>
    <Text style={styles.sectionTitle}>{title}</Text>
    {aside ? <Text style={styles.sectionAside}>{aside}</Text> : null}
  </View>
);

// `writeOn` is the unfilled sheet: a value the chart already answers still
// prints, and only the gaps become a line for staff to complete by hand. On a
// generated packet a gap is a finding, so it keeps the "Not documented" marker.
const KeyValues = ({ rows, writeOn }) =>
  rows.map((row, index) => (
    <View
      key={row.label}
      style={[styles.kvRow, index === rows.length - 1 ? styles.kvRowLast : null]}
    >
      <Text style={styles.kvLabel}>{row.label}</Text>
      {row.value ? (
        <Text style={styles.kvValue}>{row.value}</Text>
      ) : writeOn ? (
        <View style={styles.kvValue}>
          <Rule height={10} />
        </View>
      ) : (
        <View style={styles.kvValueGap}>
          <Undocumented />
        </View>
      )}
    </View>
  ));

const MED_COLUMNS = [
  { key: "name", width: "38%" },
  { key: "strength", width: "20%" },
  { key: "dose", width: "22%" },
  { key: "frequency", width: "20%" },
];

const MedHeader = () => (
  <View style={styles.medHeadRow}>
    <Text style={[styles.medHead, { width: MED_COLUMNS[0].width }]}>Medication</Text>
    <Text style={[styles.medHead, { width: MED_COLUMNS[1].width }]}>Strength</Text>
    <Text style={[styles.medHead, { width: MED_COLUMNS[2].width }]}>Dose</Text>
    <Text style={[styles.medHead, { width: MED_COLUMNS[3].width }]}>Freq</Text>
  </View>
);

const MedRow = ({ med, blank }) => (
  <View style={styles.medRow} wrap={false}>
    <View style={[styles.medNameWrap, { width: MED_COLUMNS[0].width }]}>
      {blank ? (
        <View style={{ flex: 1, paddingRight: 4 }}>
          <Rule height={10} />
        </View>
      ) : (
        <>
          <Text style={styles.medName}>{med.name}</Text>
          {med.narcotic ? (
            <View style={styles.narcotic}>
              <Text style={styles.narcoticText}>C-II</Text>
            </View>
          ) : null}
        </>
      )}
    </View>
    {MED_COLUMNS.slice(1).map((column) => (
      <View key={column.key} style={{ width: column.width, paddingRight: 4 }}>
        {blank ? (
          <Rule height={10} />
        ) : (
          <Text style={styles.medCell}>{med[column.key] || EMPTY}</Text>
        )}
      </View>
    ))}
  </View>
);

// On an unfilled sheet the chart's own items still print and the list is padded
// out to `minRows` with write-on lines, so staff can add what the chart misses.
const BulletList = ({ items, muted, writeOn, minRows = 0 }) => {
  const padding = writeOn ? Math.max(minRows - items.length, 0) : 0;

  if (!items.length && !padding) {
    return <Text style={styles.emptyNote}>{NOT_DOCUMENTED}</Text>;
  }

  return (
    <View style={styles.listGrid}>
      {items.map((item, index) => (
        <View key={`${item}-${index}`} style={styles.listItem}>
          <View style={[styles.listBullet, muted ? styles.listBulletMuted : null]} />
          <Text style={muted ? styles.listTextMuted : styles.listText}>{item}</Text>
        </View>
      ))}
      {Array.from({ length: padding }).map((_, index) => (
        <View key={`write-${index}`} style={styles.listItem}>
          <View style={[styles.listBullet, muted ? styles.listBulletMuted : null]} />
          <View style={{ flex: 1 }}>
            <Rule height={10} />
          </View>
        </View>
      ))}
    </View>
  );
};

const CheckBox = ({ checked }) => (
  <View style={styles.box}>{checked ? <Text style={styles.boxMark}>X</Text> : null}</View>
);

// A write-on field. Any value staff already captured is printed on the line;
// the line itself always stays, because the crew adds to this sheet by hand.
const WriteField = ({ label, value, minHeight }) => (
  <View>
    {label ? <Text style={styles.fieldLabel}>{label}</Text> : null}
    <View style={[styles.writeLine, minHeight ? { minHeight } : null]}>
      {value ? <Text style={styles.writeValue}>{value}</Text> : null}
    </View>
  </View>
);

const CriticalCell = ({
  label,
  value,
  note,
  documented,
  small,
  width,
  last,
  writeOn,
}) => (
  <View style={[styles.critCell, { width }, last ? styles.critCellLast : null]}>
    <Text style={styles.critLabel}>{label}</Text>
    {documented ? (
      <Text style={[small ? styles.critValueSmall : styles.critValue, styles.flag]}>
        {value}
      </Text>
    ) : writeOn ? (
      // Nothing has been recorded yet on an unfilled sheet, so this is a field
      // to complete, not a finding. Two rules keep the band the same height as
      // a cell carrying a value and its note.
      <>
        <View style={{ marginTop: 4 }}>
          <Rule height={14} />
        </View>
        <View style={{ marginTop: 4 }}>
          <Rule height={11} />
        </View>
      </>
    ) : (
      <View style={styles.undoc}>
        <Text style={styles.undocText}>{NOT_DOCUMENTED}</Text>
      </View>
    )}
    {note ? <Text style={styles.critNote}>{note}</Text> : null}
  </View>
);

const EMSFormPDF = ({
  clientData,
  profile,
  formData,
  // Letter is the design's page size. Legal stays available because the sheet
  // was previously printable at other sizes.
  pageSize = "LETTER",
  blank = false,
}) => {
  const sheet = buildEmsSheetData({
    clientDetails: clientData,
    profile,
    formData,
  });

  const { org, client, critical, scene } = sheet;
  // The masthead names the sending facility, never the parent business.
  const orgLine = [org.facilityName, org.facilityAddress, org.facilityPhone]
    .filter(Boolean)
    .join("  |  ");

  const inlineMeds = sheet.medications.slice(0, MED_ROWS_INLINE);
  const overflowMeds = sheet.medications.slice(MED_ROWS_INLINE);
  const inlineDiagnoses = sheet.diagnoses.slice(0, DIAGNOSES_INLINE);
  const overflowDiagnoses = sheet.diagnoses.slice(DIAGNOSES_INLINE);

  const identity = [
    { label: "Sex", value: client.sex },
    { label: "Date of birth", value: client.dobWithAge },
    { label: "Weight", value: client.weight },
    { label: "Room", value: client.room },
  ];

  const isolationValue =
    critical.isolation === "None in effect, standard precautions"
      ? "None in effect"
      : critical.isolation;

  // Pad the chart's medications out to a full block of rows on an unfilled
  // sheet so there is somewhere to write what the MAR does not carry yet.
  const medWriteRows = blank ? Math.max(8 - inlineMeds.length, 0) : 0;

  return (
    <Document
      title={`${client.name || "Client"} EMS Transfer Sheet`}
      author={org.facilityName || org.companyName || "Ogiso"}
    >
      <Page size={pageSize} style={styles.page}>
        {/* Continuation header. Renders from page 2 onward so a long med list or
            narrative still identifies the client on every sheet. */}
        <View
          fixed
          render={({ pageNumber }) =>
            pageNumber > 1 ? (
              <View style={styles.contBar}>
                <Text style={styles.contName}>
                  {`${client.name || "Client"}  |  ${
                    client.dob || EMPTY
                  }  |  ${critical.codeStatus}`}
                </Text>
                <Text style={styles.contMeta}>
                  {`EMS Transfer Sheet, continued  |  ${sheet.recordId}`}
                </Text>
              </View>
            ) : null
          }
        />

        <View style={styles.mast}>
          <View style={styles.mastLeft}>
            <Text style={styles.mastTitle}>EMS TRANSFER SHEET</Text>
            {orgLine ? <Text style={styles.mastOrg}>{orgLine}</Text> : null}
          </View>
          <View style={styles.mastMeta}>
            {blank ? (
              <>
                <Text>
                  Printed <Text style={styles.mastMetaStrong}>{sheet.printedAt}</Text>
                </Text>
                <Text>{`Client ID ${sheet.recordId}`}</Text>
                <View style={styles.stampWrap}>
                  <Text style={styles.stampText}>Complete by hand</Text>
                </View>
              </>
            ) : (
              <>
                <Text>
                  Printed <Text style={styles.mastMetaStrong}>{sheet.printedAt}</Text>
                </Text>
                <Text
                  render={({ pageNumber, totalPages }) =>
                    `Client ID ${sheet.recordId}  |  Page ${pageNumber} of ${totalPages}`
                  }
                />
                <Text>Hand this sheet to the responding crew</Text>
              </>
            )}
          </View>
        </View>

        <View style={styles.ident}>
          {client.name ? (
            <Text style={styles.identName}>{client.name}</Text>
          ) : (
            <View style={{ flex: 1, paddingRight: 12 }}>
              {blank ? <Rule height={22} /> : <Undocumented size={10} />}
            </View>
          )}
          <View style={styles.identList}>
            {identity.map((item) => (
              <View key={item.label} style={styles.identCell}>
                <Text style={styles.identLabel}>{item.label}</Text>
                {item.value ? (
                  <Text style={styles.identValue}>{item.value}</Text>
                ) : blank ? (
                  <View style={{ width: 66, marginTop: 2 }}>
                    <Rule height={13} />
                  </View>
                ) : (
                  <View style={{ marginTop: 2 }}>
                    <Undocumented />
                  </View>
                )}
              </View>
            ))}
          </View>
        </View>

        {/* Code status and isolation are only ever answered by staff at the
            time of transfer, so an unfilled sheet leaves them as rules. The
            allergy list comes from the chart, so it prints either way, and its
            note is the one line that is still true of an unfilled sheet. */}
        <View style={styles.crit}>
          <CriticalCell
            width="30%"
            label="Code status"
            value={critical.codeStatus}
            note={blank ? "" : critical.codeNote}
            documented={!blank && critical.codeDocumented}
            writeOn={blank}
          />
          <CriticalCell
            width="40%"
            label="Allergies"
            value={critical.allergyValue}
            note={critical.allergyNote}
            documented={critical.allergyDocumented}
            writeOn={blank}
            small
          />
          <CriticalCell
            width="30%"
            label="Isolation / airborne"
            value={isolationValue}
            note={blank ? "" : critical.isolationNote}
            documented={!blank && critical.isolationDocumented}
            writeOn={blank}
            small
            last
          />
        </View>

        <View style={styles.cols}>
          <View style={styles.colLeft}>
            <View style={styles.section}>
              <SectionHead title="Active diagnoses" aside="from chart" />
              <BulletList items={inlineDiagnoses} writeOn={blank} minRows={6} />
            </View>

            <View style={styles.section}>
              <SectionHead
                title="Current medications"
                aside={
                  blank
                    ? `${sheet.medications.length} from chart, confirm at transfer`
                    : `${sheet.medications.length} active${
                        sheet.medicationsVerified ? "" : ", not reconciled"
                      }`
                }
              />
              <MedHeader />
              {inlineMeds.map((med, index) => (
                <MedRow key={`${med.name}-${index}`} med={med} />
              ))}
              {Array.from({ length: medWriteRows }).map((_, index) => (
                <MedRow key={`write-med-${index}`} med={{}} blank />
              ))}
              {!blank && !sheet.medications.length ? (
                <Text style={[styles.emptyNote, { marginTop: 3 }]}>
                  Not documented in the chart. Confirm the list with the pharmacy.
                </Text>
              ) : null}
              {blank ? (
                sheet.medications.length ? (
                  <Text style={styles.medFoot}>
                    Printed from the chart. Confirm against the current MAR before
                    transfer and add anything missing above.
                  </Text>
                ) : null
              ) : (
                <Text style={styles.medFoot}>{sheet.medicationFootnote}</Text>
              )}
            </View>

            <View style={styles.section}>
              <SectionHead title="Assistive devices" aside="travel with client" />
              <BulletList items={sheet.devices} muted writeOn={blank} minRows={4} />
            </View>
          </View>

          <View style={styles.colRight}>
            <View style={styles.section}>
              <SectionHead title="Emergency contacts" />
              <KeyValues rows={sheet.contacts} writeOn={blank} />
            </View>

            <View style={styles.section}>
              <SectionHead title="Insurance and pharmacy" />
              <KeyValues rows={sheet.coverage} writeOn={blank} />
            </View>

            <View style={styles.section}>
              <SectionHead title="Sending facility" />
              <KeyValues rows={sheet.facility} writeOn={blank} />
            </View>

            <View style={styles.section}>
              <SectionHead title="Documents sent with client" />
              {sheet.attachments.map((item) => (
                <View key={item.label} style={styles.attRow}>
                  <CheckBox checked={!blank && item.checked} />
                  <Text style={styles.attText}>{item.label}</Text>
                </View>
              ))}
            </View>
          </View>
        </View>

        {overflowDiagnoses.length ? (
          <View style={styles.section}>
            <SectionHead title="Active diagnoses, continued" aside="from chart" />
            <BulletList items={overflowDiagnoses} />
          </View>
        ) : null}

        {overflowMeds.length ? (
          <View style={styles.section}>
            <SectionHead
              title="Current medications, continued"
              aside={`${MED_ROWS_INLINE + 1} to ${sheet.medications.length}`}
            />
            <MedHeader />
            {overflowMeds.map((med, index) => (
              <MedRow key={`overflow-${med.name}-${index}`} med={med} />
            ))}
          </View>
        ) : null}

        <View style={styles.scene}>
          <View style={styles.sceneHead}>
            <Text style={styles.sectionTitle}>Complete at time of emergency</Text>
            <Text style={styles.sectionAside}>staff writes on this sheet</Text>
          </View>

          <WriteField
            label="Reason EMS was requested"
            value={scene.reason}
            minHeight={scene.reason ? 26 : 15}
          />

          <View style={styles.gridRow}>
            {scene.times.map((item) => (
              <View key={item.label} style={styles.gridCell}>
                <WriteField label={item.label} value={item.value} />
              </View>
            ))}
          </View>

          <Text style={styles.fieldLabel}>Vitals at transfer</Text>
          <View style={styles.gridRow}>
            {scene.vitals.map((item) => (
              <View key={item.label} style={[styles.gridCell, { paddingRight: 8 }]}>
                <WriteField label={item.label} value={item.value} />
              </View>
            ))}
          </View>

          <View style={styles.inlineChecks}>
            <CheckBox checked={!blank && scene.notTransported} />
            <Text style={styles.inlineCheckText}>Client was not transported</Text>
            <CheckBox checked={!blank && scene.repNotified} />
            <Text style={styles.inlineCheckText}>Authorized rep notified</Text>
          </View>

          <View style={styles.gridRow}>
            <View style={[styles.gridCell, { flex: 1.4 }]}>
              <WriteField
                label="Receiving hospital"
                value={
                  scene.notTransported
                    ? "Client was not transported"
                    : [scene.hospital, scene.transportedBy].filter(Boolean).join(" / ")
                }
              />
            </View>
            <View style={styles.gridCell}>
              <WriteField label="Rep notified by" value={scene.contactedBy} />
            </View>
            <View style={styles.gridCell}>
              <WriteField label="Date and time" value={scene.notifiedAt} />
            </View>
          </View>

          <View style={styles.gridRow}>
            <View style={styles.gridCell}>
              <WriteField label="Staff name and signature" value={scene.completedBy} />
            </View>
            <View style={styles.gridCell}>
              <WriteField label="Date and time signed" value={scene.completedAt} />
            </View>
          </View>
        </View>

        <View style={styles.footer} fixed>
          <Text style={styles.footerText}>
            {[org.facilityName, "EMS Transfer Sheet"].filter(Boolean).join("  |  ")}
          </Text>
          <Text
            style={styles.footerText}
            render={({ pageNumber, totalPages }) =>
              `Generated by Ogiso  |  ${sheet.printedAt}  |  Page ${pageNumber} of ${totalPages}`
            }
          />
        </View>
      </Page>
    </Document>
  );
};

export default EMSFormPDF;
